import { InspectionItemType } from './inspection-item-type.enum';

/**
 * Declares that an inspection item's entered value writes back to a field on a related
 * entity when the inspection is transferred.
 *
 * These values must match the backend enum exactly:
 * - Backend: Leo.Cars.Raptor.Model/InspectionTemplates/InspectionBindings/InspectionItemBinding.cs
 */
export enum InspectionItemBinding {
    /** No write-back. Default for every item. */
    None = 'None',

    /** Writes Vehicle.Mileage, plus Odometer on the transferred Quote or WorkOrder. */
    VehicleMileage = 'VehicleMileage',

    /** Writes Vehicle.NextServiceDate. */
    NextServiceDate = 'NextServiceDate',
}

/**
 * Client mirror of a backend binding descriptor. `compatibleItemTypes` must match
 * BindingDescriptor.CompatibleItemTypes -- the API rejects a mismatched binding/type pair on
 * save, so offering one here that the backend refuses produces an error the admin cannot act on.
 */
export interface InspectionItemBindingDescriptor {
    binding: InspectionItemBinding;
    label: string;
    compatibleItemTypes: InspectionItemType[];
}

export const INSPECTION_ITEM_BINDINGS: InspectionItemBindingDescriptor[] = [
    {
        binding: InspectionItemBinding.VehicleMileage,
        label: 'Vehicle Mileage',
        compatibleItemTypes: [InspectionItemType.NumericRange],
    },
    {
        binding: InspectionItemBinding.NextServiceDate,
        label: 'Next Service Date',
        compatibleItemTypes: [InspectionItemType.DateInput],
    },
];

/**
 * Mirrors InspectionItemBindings.IsCompatible on the backend, including the None escape hatch.
 */
export function isBindingCompatible(binding: InspectionItemBinding, itemType: InspectionItemType): boolean {
    return (
        binding === InspectionItemBinding.None ||
        (INSPECTION_ITEM_BINDINGS.find((d) => d.binding === binding)?.compatibleItemTypes.includes(itemType) ?? false)
    );
}

export interface InspectionItemBindingOption {
    value: InspectionItemBinding;
    label: string;
}

const BINDING_OPTIONS_BY_ITEM_TYPE = new Map<InspectionItemType, readonly InspectionItemBindingOption[]>();

/**
 * Bindings selectable for an item of the given type, in Kendo's { value, label } shape.
 * None is not included -- it is supplied by the dropdown's [defaultItem], matching how the
 * Job Category dropdown offers its empty option. An empty result means the type supports no
 * bindings and the control is hidden rather than rendered empty.
 *
 * Results are memoized per item type because this is called from a template binding, which
 * re-evaluates on every change-detection cycle. Returning a freshly built array each time hands
 * the dropdownlist a new [data] reference on every cycle, which resets its selection the instant
 * the user picks something.
 */
export function bindingOptionsFor(itemType: InspectionItemType): readonly InspectionItemBindingOption[] {
    const cached = BINDING_OPTIONS_BY_ITEM_TYPE.get(itemType);
    if (cached) {
        return cached;
    }

    const options = Object.freeze(
        INSPECTION_ITEM_BINDINGS.filter((d) => d.compatibleItemTypes.includes(itemType)).map((d) => ({
            value: d.binding,
            label: d.label,
        })),
    );
    BINDING_OPTIONS_BY_ITEM_TYPE.set(itemType, options);

    return options;
}
